import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CompanyResponse, DistributionChannelsService, MarketsService, RetailerTypesService, RetailersService, RetailerRequest, BusinessGroupsService, RetailerResponse, BrandsService, AttributeRequest } from '@src/app/shared/backend/clients';
import { AttributeValuesFormComponent } from '@src/app/shared/components/attribute-values-form/attribute-values-form.component';
import { DynamicAttributeDefinition, DynamicAttributeSelection } from '@src/app/shared/components/attribute-values-form/attribute-values-form.model';
import { RetailerAttributeValuesAdapter } from '../retailer-attribute-values.adapter';
import { CountryService } from '@src/app/shared/services/countries-service';
import { SessionService } from '@src/app/shared/services/session.service';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { PermissionService } from '@src/app/shared/services/permission.service';

@Component({
    selector: 'app-edit-retailer',
    templateUrl: './edit-retailer.component.html',
    styleUrl: './edit-retailer.component.scss',
    host: { class: 'app-edit-retailer' },
    standalone: false
})
export class EditRetailerComponent extends BaseViewComponent implements OnInit {

  @ViewChild('retailerAttributeValuesForm') retailerAttributeValuesForm?: AttributeValuesFormComponent;

  retailerAttributesFeatureEnabled = false;
  retailerAttributeDefinitions: DynamicAttributeDefinition[] = [];
  retailerAttributeSelections: DynamicAttributeSelection[] = [];

  public company: CompanyResponse | undefined;
  public countries: any[] = [];
  public languages: { language: string; code: string }[] = [];

  public companyHasCpo = false;
  public showDistributionChannel: boolean = false;

  public retailerId = 0;
  public retailer: RetailerResponse = {};

  combosData: { [key: string]: ComboData } = {
    markets: new ComboData(async (filter) => await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(filter))),
    retailerTypes: new ComboData(async (filter) => await firstValueFrom(this.retailerTypesService.apiRetailerTypesFilterComboPost(filter))),
    businessGroups: new ComboData(async (filter) => await firstValueFrom(this.businessGroupsService.apiBusinessGroupsFilterComboPost(filter))),
    distributionChannels: new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    brands: new ComboData(async (filter) => {
      const activeBrandsFilter = {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      };
      const brandsFilter = {
        ...filter,
        additionalFilters: [
          ...(filter.additionalFilters ?? []),
          activeBrandsFilter
        ]
      };
      return await firstValueFrom(this.brandsService.apiBrandsFilterComboPost(brandsFilter));
    }),

  };

  public retailerForm: FormGroup = this.fb.group({
    retailerName: ['', [Validators.required, Validators.maxLength(255)]],
    romanisedName: ['', [Validators.maxLength(255)]],
    externalRetailerId: ['', [Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    street: ['', [Validators.required, Validators.maxLength(255)]],
    zipCode: ['', [Validators.required, Validators.maxLength(50)]],
    city: ['', [Validators.required, Validators.maxLength(50)]],
    cityRomanized: ['', [latinCharsetRegexValidator()]],
    stateProvince: ['', [Validators.maxLength(255)]],
    stateProvinceRomanized: ['', [latinCharsetRegexValidator()]],
    country: [[], [Validators.required]],
    latitude: ['', [Validators.maxLength(50)]],
    longitude: ['', [Validators.maxLength(50)]],
    distributionChannelIds: [[]],
    marketId: [[], [Validators.required]],
    brandIds: [[]],
    retailerTypeId: [[]],
    businessGroupId: [[]],
    openingDate: [null],
    closingDate: [null],
    hasCpo: ['false', [Validators.required]],
    email: ['', [emailRegexValidator(), Validators.maxLength(50)]],
    comments: ['', [Validators.maxLength(255)]],
  });

  filterOperator = FilterOperator;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private session: SessionService,
    private translate: TranslateService,
    private brandsService: BrandsService,
    private retailersService: RetailersService,
    private retailerTypesService: RetailerTypesService,
    private businessGroupsService: BusinessGroupsService,
    private distributionChannelsService: DistributionChannelsService,
    private marketsService: MarketsService,
    private countryService: CountryService,
    private retailerAttributeValuesAdapter: RetailerAttributeValuesAdapter,
    private permissionService: PermissionService,
    private fb: FormBuilder,
  ) {
    super();
    this.getLanguages();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.retailerId = Number(params['retailerId']);

      if (!this.retailerId) {
        return;
      }

      this.company = await this.session.getCompanyData();
      this.countries = await this.countryService.getCountries();
      this.companyHasCpo = !!this.company?.hasCpo;
      this.showDistributionChannel = await this.permissionService.hasFeaturePermissions(Features.DistributionChannels);
      const distributionChannelControl = this.retailerForm.get('distributionChannelIds');

      if (this.showDistributionChannel) {
        distributionChannelControl?.setValidators([Validators.required]);
      } else {
        distributionChannelControl?.clearValidators();
      }
      const settings = await this.session.getCompanySettings();

      this.retailerAttributesFeatureEnabled =
        settings.features?.some(
          feature => feature.name === 'RetailerAttributes' && feature.isEnabled
        ) ?? false;

      // load retailer first (to know which ids must exist in the drodopwns)
      this.retailer = await firstValueFrom(this.retailersService.apiRetailersIdGet(this.retailerId));

      await this.initCombosData();
      await this.loadRetailerAttributeDefinitions();

      this.retailerAttributeSelections = this.retailerAttributeValuesAdapter.mapSavedValuesToSelections(this.retailer?.retailerAttributeValues);

      this.ensureSelectedComboItems();
      this.initForm();
    });
  }

  private async loadRetailerAttributeDefinitions(): Promise<void> {
    if (!this.retailerAttributesFeatureEnabled) {
      this.retailerAttributeDefinitions = [];
      return;
    }

    this.retailerAttributeDefinitions = await this.retailerAttributeValuesAdapter.getActiveDefinitions();
  }

  private async initCombosData() {
    await this.combosData['brands'].getData(null, 1);
    await this.combosData['markets'].getData(null, 1);
    await this.combosData['retailerTypes'].getData(null, 1);
    await this.combosData['distributionChannels'].getData(null, 1);

    if (this.showBusinessGroup) {
      await this.combosData['businessGroups'].getData(null, 1);
    }
  }

  getComboTotalPages(
    comboType: 'markets' | 'retailerTypes' | 'businessGroups' | 'distributionChannels' | 'brands'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  async onLazyLoadComboData(comboType: 'markets' | 'retailerTypes' | 'businessGroups' | 'distributionChannels' | 'brands', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  private ensureSelectedComboItems(): void {
    // ensure single selected item exists (by id)
    const ensureSingle = (key: string, selected: any | null) => {
      if (!selected?.id) return;
      const list = this.combosData[key].list ?? [];
      const exists = list.some((x: any) => x?.id === selected.id);
      if (!exists) this.combosData[key].list = [selected, ...list];
    };

    // ensure multiple selected items exist (by id)
    const ensureMulti = (key: string, selectedArr: any[] | null | undefined) => {
      const selected = (selectedArr ?? []).filter(x => x?.id != null);
      if (!selected.length) return;

      const list = this.combosData[key].list ?? [];
      const missing = selected.filter(s => !list.some((x: any) => x?.id === s.id));
      if (missing.length) this.combosData[key].list = [...missing, ...list];
    };

    const market = this.retailer.marketId
      ? { id: this.retailer.marketId, text: this.retailer.market?.name ?? `Market #${this.retailer.marketId}` }
      : null;

    const type = this.retailer.typeId
      ? { id: this.retailer.typeId, text: this.retailer.type?.name ?? `Type #${this.retailer.typeId}` }
      : null;

    const businessGroup = this.retailer.businessGroupId
      ? { id: this.retailer.businessGroupId, text: this.retailer.businessGroup?.name ?? `Type #${this.retailer.businessGroupId}` }
      : null;

    const dist = (this.retailer.distributionChannels ?? []).map(dc => ({
      id: dc.id,
      text: dc.name ?? `Channel #${dc.id}`
    }));

    const brands = (this.retailer.brands ?? []).map(brand => ({
      id: brand.id,
      text: brand.name ?? `Brand #${brand.id}`
    }));

    ensureSingle('markets', market);
    ensureSingle('retailerTypes', type);

    if (this.showBusinessGroup) {
      ensureSingle('businessGroups', businessGroup);
    }

    ensureMulti('distributionChannels', dist);
    ensureMulti('brands', brands);
  }

  private initForm(): void {

    const selectedCountry = this.retailer.isoCountry ? [this.retailer.isoCountry.toUpperCase()] : [];

    const openingDate = this.retailer.openingDate ? new Date(this.retailer.openingDate) : null;
    const closingDate = this.retailer.closingDate ? new Date(this.retailer.closingDate) : null;

    const selectedBrands = (this.retailer.brands ?? [])
      .map(brand => brand.id)
      .filter(id => id != null);

    const selectedDistributionChannels = (this.retailer.distributionChannels ?? [])
      .map(dc => dc.id)
      .filter(id => id != null);

    this.retailerForm.patchValue({
      retailerName: this.retailer.name ?? '',
      romanisedName: this.retailer.nameRomanised ?? '',
      externalRetailerId: this.retailer.externalPosId ?? '',
      code: this.retailer.code ?? '',

      street: this.retailer.street ?? '',
      zipCode: this.retailer.zip ?? '',
      city: this.retailer.city ?? '',
      cityRomanized: this.retailer.cityRomanised ?? '',
      stateProvince: this.retailer.stateProvince ?? '',
      stateProvinceRomanized: this.retailer.stateProvinceRomanised ?? '',
      country: selectedCountry,

      latitude: this.retailer.latitude ?? '',
      longitude: this.retailer.longitude ?? '',

      brandIds: selectedBrands,
      marketId: this.retailer.marketId ? [this.retailer.marketId] : [],
      retailerTypeId: this.retailer.typeId ? [this.retailer.typeId] : [],
      businessGroupId: this.retailer.businessGroupId ? [this.retailer.businessGroupId] : [],
      distributionChannelIds: selectedDistributionChannels,

      openingDate,
      closingDate,
      hasCpo: this.retailer.hasCpo ? 'true' : 'false',
      email: this.retailer.email ?? '',
      comments: this.retailer.comment ?? '',
    });
  }

  private getLanguages() {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(l => l.isEnabled);

    const translation = this.translate.instant('PORTAL_LANGUAGES');
    this.languages = enabledLanguages.map(lang => ({
      language: translation[(lang.code || 'unknown').toUpperCase()],
      code: lang.code || 'unknown'
    }));
  }

  get showBusinessGroup(): boolean {
    return this.company?.hasRetailerBusinessGroup === true;
  }

  async submitForm(): Promise<void> {
    this.retailerForm.markAllAsTouched();
    this.retailerAttributeValuesForm?.markAllAsTouched();

    const retailerAttributesFormIsInvalid = this.retailerAttributeValuesForm?.attributeValuesForm.invalid ?? false;

    if (this.retailerForm.invalid || retailerAttributesFormIsInvalid) {
      return;
    }

    const v = this.retailerForm.value;

    const brandIds = v.brandIds ?? [];
    const distributionChannelIds = v.distributionChannelIds ?? [];

    const openingDate = v.openingDate ? new Date(v.openingDate).toISOString() : null;
    const closingDate = v.closingDate ? new Date(v.closingDate).toISOString() : null;

    let retailerAttributes: AttributeRequest[] | undefined;

    if (this.retailerAttributesFeatureEnabled) {
      const attributeForm = this.retailerAttributeValuesForm;

      if (attributeForm) {
        const selections = attributeForm.getSelections();

        retailerAttributes = this.retailerAttributeValuesAdapter.mapSelectionsToRequest(selections);
      }
    }

    const payload: RetailerRequest = {
      code: v.code,
      name: v.retailerName,
      street: v.street,
      zip: v.zipCode,
      city: v.city,
      stateProvince: v.stateProvince,
      isoCountry: v.country?.[0],
      latitude: v.latitude,
      longitude: v.longitude,
      hasCpo: v.hasCpo === 'true',
      brandIds,
      distributionChannelIds,
      externalPosId: v.externalRetailerId,
      nameRomanised: v.romanisedName,
      cityRomanised: v.cityRomanized,
      stateProvinceRomanised: v.stateProvinceRomanized,
      openingDate,
      closingDate,
      email: v.email,
      marketId: v.marketId?.[0] ?? 0,
      typeId: v.retailerTypeId?.[0],
      businessGroupId: this.showBusinessGroup ? v.businessGroupId?.[0] : undefined,
      comment: v.comments,
      retailerAttributes
    };

    // console.log('Final retailer payload:', JSON.stringify(payload, null, 2));

    await firstValueFrom(this.retailersService.apiRetailersUpdateIdPut(this.retailerId, payload));

    this.router.navigate(['/retailers', this.retailerId]);
  }
}
